// Searching and tag indexing over a set of {@link Model}s — pure functions with
// no environment dependency, so the viewer and the MCP server rank results
// identically. A query is matched against id, name, tags, summary and
// description with a simple weighted score; tags can additionally hard-filter.

import type { Model } from "./types";

export interface SearchOptions {
  /** Free-text query (matched across id, name, tags, summary, description). */
  query?: string;
  /** Require every one of these tags (case-insensitive). */
  tags?: string[];
  /** Cap the number of results (default: all matches). */
  limit?: number;
}

export interface SearchHit {
  model: Model;
  score: number;
}

interface Haystack {
  name: string;
  id: string;
  tags: string;
  tagsLower: string[];
  text: string;
}

const indexCache = new WeakMap<Model, Haystack>();

function indexed(m: Model): Haystack {
  let h = indexCache.get(m);
  if (h) return h;
  const tagsLower = m.tags.map((t) => t.toLowerCase());
  h = {
    name: m.name.toLowerCase(),
    id: m.id.toLowerCase(),
    tagsLower,
    tags: tagsLower.join(" "),
    text: `${m.summary ?? ""} ${m.description ?? ""}`.toLowerCase(),
  };
  indexCache.set(m, h);
  return h;
}

function scoreTerm(h: Haystack, term: string): number {
  let s = 0;
  if (h.id === term || h.name === term) s += 100;
  if (h.tagsLower.includes(term)) s += 60;
  if (h.name.includes(term)) s += 30;
  if (h.id.includes(term)) s += 20;
  if (h.tags.includes(term)) s += 15;
  if (h.text.includes(term)) s += 5;
  return s;
}

/**
 * Rank `models` for the given options. With no query and no tags, returns every
 * model sorted by name. Tag filters are ANDed; the text query (if any) further
 * ranks the survivors and drops the ones it does not match at all.
 */
export function search(models: Model[], opts: SearchOptions = {}): SearchHit[] {
  const want = (opts.tags ?? []).map((t) => t.toLowerCase()).filter(Boolean);
  let pool: Model[];
  if (want.length === 0) {
    pool = models;
  } else {
    pool = models.filter((m) => {
      const h = indexed(m);
      return want.every((t) => h.tagsLower.includes(t));
    });
  }

  const query = opts.query?.trim().toLowerCase();
  let hits: SearchHit[];
  if (!query) {
    hits = pool.map((model) => ({ model, score: 0 })).sort((a, b) => a.model.name.localeCompare(b.model.name));
  } else {
    const terms = query.split(/\s+/).filter(Boolean);
    hits = pool
      .map((model) => {
        const h = indexed(model);
        return { model, score: terms.reduce((sum, t) => sum + scoreTerm(h, t), 0) };
      })
      .filter((h) => h.score > 0)
      .sort((a, b) => b.score - a.score || a.model.name.localeCompare(b.model.name));
  }

  return opts.limit != null ? hits.slice(0, Math.max(0, opts.limit)) : hits;
}

/** Every tag across `models` with how many models carry it, most common first. */
export function tagCounts(models: Model[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const m of models) for (const t of m.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}
